import type { RefObject } from "react";
import type { OrbitControls as OrbitControlsImpl } from "three-stdlib";

const ROTATE_STEP = Math.PI / 4; // 수평 회전 45° (azimuth는 360° 순환이라 단방향)
const TILT_TOPDOWN = 0.18; // 거의 위에서
const TILT_ANGLED = 1.0; // ~57° 비스듬히
const ZOOM_FACTOR = 1.25;

/** OrbitControls 조작 핸들러 모음 (회전/틸트/줌/정북). */
export function useViewerControls(controlsRef: RefObject<OrbitControlsImpl | null>) {
  function resetNorth() {
    const c = controlsRef.current;
    if (!c) return;
    // azimuth 0 = 북 (gpsToLocal에서 -z=북, 카메라가 +z에 있어야 북을 본다)
    c.setAzimuthalAngle(0);
    c.update();
  }

  function rotate() {
    const c = controlsRef.current;
    if (!c) return;
    c.setAzimuthalAngle(c.getAzimuthalAngle() + ROTATE_STEP);
    c.update();
  }

  function toggleTilt() {
    const c = controlsRef.current;
    if (!c) return;
    // polar은 0~85.7°로 막혀 순환 불가 → "위에서 ↔ 비스듬히" 토글
    const next = c.getPolarAngle() > (TILT_TOPDOWN + TILT_ANGLED) / 2 ? TILT_TOPDOWN : TILT_ANGLED;
    c.setPolarAngle(Math.min(c.maxPolarAngle, Math.max(c.minPolarAngle, next)));
    c.update();
  }

  // OrbitControls엔 공개 zoom API가 없어 target→camera 거리를 직접 조절.
  function dolly(factor: number) {
    const c = controlsRef.current;
    if (!c) return;
    const offset = c.object.position.clone().sub(c.target);
    const max = Number.isFinite(c.maxDistance) ? c.maxDistance : 5000;
    const dist = Math.min(max, Math.max(c.minDistance, offset.length() * factor));
    offset.setLength(dist);
    c.object.position.copy(c.target).add(offset);
    c.update();
  }

  return {
    resetNorth,
    rotate,
    toggleTilt,
    zoomIn: () => dolly(1 / ZOOM_FACTOR),
    zoomOut: () => dolly(ZOOM_FACTOR),
  };
}
